import React, { useEffect } from "react";
import { Box, IconButton, Modal, Typography, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { getCustomerInsights } from "../../../../../store/crm/churn";
import {
  getGotoConversation,
  sendGotoSms,
  markGotoRead,
  receiveGotoMessage,
} from "../../../../../store/crm/goto-sms";
import { gotoSmsApi } from "../../../../../store/crm/goto-sms/gotoSmsApi";
import ChatThread from "./ChatThread";
import ChatComposer from "./ChatComposer";
import ChatInsightsSidebar from "./ChatInsightsSidebar";
import {
  ACCENT,
  ACCENT_LIGHT,
  PINK_BORDER,
  drawerIconChip,
} from "../../churnStyles";

const ChatModal = ({ open, onClose, customer }) => {
  const dispatch = useDispatch();
  const customerId = customer?.user_id;
  const { conversations, loading, sending } = useSelector(
    (state) => state.gotoSmsStore
  );
  const { insights, loading: churnLoading } = useSelector(
    (state) => state.churnStore
  );
  const hideSidebar = useMediaQuery("(max-width:900px)");

  const conversation = customerId ? conversations[customerId] : null;
  const messages = conversation?.messages || [];

  // Load thread + insights, mark read, and subscribe for live inbound
  // messages while the modal is open.
  useEffect(() => {
    if (!open || !customerId) return undefined;
    dispatch(getGotoConversation({ customerId }));
    dispatch(getCustomerInsights(customerId));
    dispatch(markGotoRead(customerId));
    const unsubscribe = gotoSmsApi.subscribe(customerId, (message) => {
      dispatch(receiveGotoMessage({ customerId, message }));
    });
    return unsubscribe;
  }, [open, customerId, dispatch]);

  const handleSend = async (body, attachments) => {
    const formData = new FormData();
    formData.append("customer_id", customerId);
    formData.append("body", body);
    attachments.forEach((file) => formData.append("media[]", file));
    const res = await dispatch(sendGotoSms({ customerId, formData }));
    return !!res?.payload?.row;
  };

  const handleLoadOlder = () => {
    if (conversation?.has_more && !loading) {
      dispatch(
        getGotoConversation({ customerId, before: conversation.oldest_cursor })
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", md: "90vw" },
          maxWidth: 1280,
          height: "85vh",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 25px 50px -12px rgba(15,23,42,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${PINK_BORDER}`,
            flexShrink: 0,
          }}
        >
          <Box sx={drawerIconChip}>
            <SmsOutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "17px",
                color: "#1a1a2e",
                fontFamily: "helvetica-lt-std-bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Messages — {customer?.name}
            </Typography>
            {customer?.phone && (
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                {customer.phone}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              color: "#6B7280",
              "&:hover": { background: ACCENT_LIGHT, color: ACCENT },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* body: thread + insights sidebar */}
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              flex: 2,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {conversation?.no_phone ? (
              <Typography sx={{ color: "#9CA3AF", fontSize: "14px", textAlign: "center", my: 6 }}>
                This customer has no phone number on file — add one in their
                profile to start messaging.
              </Typography>
            ) : (
              <ChatThread
                messages={messages}
                loading={loading}
                hasMore={!!conversation?.has_more}
                onLoadOlder={handleLoadOlder}
              />
            )}
            <ChatComposer
              onSend={handleSend}
              sending={sending}
              disabled={!!conversation?.no_phone}
              templateContext={{ user_id: customerId, name: customer?.name }}
            />
          </Box>
          {!hideSidebar && (
            <Box
              sx={{
                flex: 1,
                borderLeft: `1px solid ${PINK_BORDER}`,
                minWidth: 0,
                background: "#FFFDFE",
              }}
            >
              <ChatInsightsSidebar
                data={customerId ? insights[customerId] : null}
                loading={churnLoading.insights}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ChatModal;
